import { Form, Formik } from "formik";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Logo from "../../components/common/Logo";
import Message from "../../components/common/Message";
import { Heading } from "../../components/common/Typography";
import SpringContainer from "../../components/containers/SpringContainer";
import Button, { ButtonVariant } from "../../components/form/Button";
import Input from "../../components/form/Input";
import TurnstileWidget from "../../components/form/TurnstileWidget";
import { ENVIRONMENT } from "../../config";
import { ROUTE_LOGIN, ROUTE_SUCCESSFUL_SIGN_UP } from "../../constants/routes";
import { useRegisterMutation } from "../../redux/services/auth";
import { browserTimeZone } from "../../utils";
import PasswordValidator from "./components/PasswordValidator";

const signupSchema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email address").required("Required"),
  password: Yup.string()
    .required("Required")
    .matches(
      /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]).{8,}$/,
      "Password must meet the guidelines"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Required"),
});

type signUpFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const initialValues: signUpFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

const whitelistedAliasDomains = [
  "codebase.com",
  "adogy.com",
  "articlex.com",
  "relevance.com",
  "calendar.com",
];

const SignUpEmail = () => {
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();
  const [signUpError, setSignUpError] = useState("");
  const [cfTurnstileToken, setCfTurnstileToken] = useState<string>("");

  const handleSignUp = useCallback(
    async (values: signUpFormValues) => {
      if (["stage", "prod"].includes(ENVIRONMENT!) && !cfTurnstileToken) {
        return;
      }
      setSignUpError("");
      const { firstName, lastName, email, password } = values;

      const emailParts = email.split("@");
      const domain = emailParts[emailParts.length - 1];

      if (email.includes("+") && !whitelistedAliasDomains.includes(domain)) {
        setSignUpError("Email addresses with '+' are not allowed.");
        return;
      }

      await register({
        firstName,
        lastName,
        email,
        password,
        timezone: browserTimeZone,
        isSuper: false,
        cfTurnstileToken,
      })
        .unwrap()
        .then((res) => {
          if (res?.user_id) {
            navigate(
              ROUTE_SUCCESSFUL_SIGN_UP + `?email=${encodeURIComponent(email)}`
            );
          } else {
            setSignUpError("Something went wrong. Please contact support.");
          }
        })
        .catch((error) => {
          if (error?.status === 400 && error?.data?.detail) {
            setSignUpError(error.data.detail);
          } else {
            setSignUpError("Something went wrong. Please contact support.");
          }
        });
    },
    [navigate, register, cfTurnstileToken]
  );

  const navigateToLogin = useCallback(() => navigate(ROUTE_LOGIN), [navigate]);

  return (
    <SpringContainer
      centered
      className="relative"
    >
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => handleSignUp(values)}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={signupSchema}
      >
        {(props) => {
          const { values, errors, setFieldValue } = props;
          const stageOrProd =
            ENVIRONMENT && ["stage", "prod"].includes(ENVIRONMENT);
          let buttonVariant: ButtonVariant;
          if (stageOrProd && !cfTurnstileToken) {
            buttonVariant = "disabled";
          } else {
            buttonVariant = "primary";
          }
          return (
            <div className="mx-auto w-full max-w-sm space-y-6 text-center">
              <Logo />
              <Heading size="xl">Create a free account</Heading>
              <Message
                type="error"
                message={signUpError}
                className="justify-center"
              />
              <Form className="space-y-6">
                <div className="flex gap-6 flex-col md:gap-2 md:flex-row">
                  <Input
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    type="text"
                    value={values.firstName}
                    errorMessage={errors?.firstName}
                    onChange={(e) => setFieldValue("firstName", e.target.value)}
                  />
                  <Input
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    type="text"
                    value={values.lastName}
                    errorMessage={errors?.lastName}
                    onChange={(e) => setFieldValue("lastName", e.target.value)}
                  />
                </div>
                <Input
                  id="email"
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  errorMessage={errors?.email}
                  onChange={(e) => setFieldValue("email", e.target.value)}
                />
                <div>
                  <Input
                    id="password"
                    label="Password"
                    name="password"
                    type="password"
                    value={values.password}
                    errorMessage={errors?.password}
                    onChange={(e) => {
                      const password = e.target.value;
                      setFieldValue("password", password);
                    }}
                  />
                  <PasswordValidator password={values.password} />
                </div>
                <Input
                  id="confirmPassword"
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={values.confirmPassword}
                  errorMessage={errors?.confirmPassword}
                  onChange={(e) =>
                    setFieldValue("confirmPassword", e.target.value)
                  }
                />
                {stageOrProd && (
                  <TurnstileWidget setToken={setCfTurnstileToken} />
                )}
                <Button
                  fullWidth
                  type="submit"
                  variant={buttonVariant}
                  onClick={() => {}}
                  loading={isLoading}
                >
                  Sign Up
                </Button>
              </Form>
            </div>
          );
        }}
      </Formik>
      <div className="absolute bottom-4">
        <p className="text-center text-sm text-gray-500">
          Already a member?{" "}
          <Button
            paddingZero
            variant="transparent-primary"
            onClick={navigateToLogin}
          >
            Sign In
          </Button>
        </p>
        <p className="text-2xs text-gray-500">
          By signing up you agree to our{" "}
          <a
            href="https://www.articlex.com/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            Terms and Conditions
          </a>
        </p>
      </div>
    </SpringContainer>
  );
};

export default SignUpEmail;
